import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Undo2, Redo2, Download, Sparkles, RotateCcw, FolderOpen, Boxes, LogIn, LogOut, Lock, ShoppingBag, Shield } from 'lucide-react';
import { useStudio } from '@/lib/store';
import { useAuth } from '@/lib/auth';
import { WorkspaceSwitcher } from '@/components/freehand/FreehandTopbar';
import { SendToFreehandButton } from '@/components/studio/SendToFreehand';
import AdminDrawer from '@/components/admin/AdminDrawer';

export default function Topbar({ onExport, onOpenGallery, onOpenUpgrade }) {
  const undo = useStudio((s) => s.undo);
  const redo = useStudio((s) => s.redo);
  const reset = useStudio((s) => s.reset);
  const historyLen = useStudio((s) => s.history.length);
  const futureLen = useStudio((s) => s.future.length);

  const { user, isPro, isAdmin, login, logout } = useAuth();
  const [adminOpen, setAdminOpen] = useState(false);

  return (
    <header
      className="h-14 shrink-0 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex items-center px-2 sm:px-4 gap-2 sm:gap-3"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2 sm:pr-3 sm:border-r sm:border-slate-200 min-w-0">
        <div className="w-9 h-9 flex items-center justify-center overflow-hidden shrink-0" data-testid="topbar-logo">
          <img
            src="/logo.png"
            alt="Tudor Balls"
            className="w-9 h-9 object-contain"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextSibling.style.display = 'flex';
            }}
          />
          <span className="w-8 h-8 rounded-xl bg-slate-900 hidden items-center justify-center">
            <Boxes className="w-4 h-4 text-white" />
          </span>
        </div>
        <div className="leading-tight hidden sm:block">
          <div className="text-sm font-semibold text-slate-900 tracking-tight">Tudor Balls</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Ball Designer</div>
        </div>
      </div>

      <a
        href="https://shop.tudorballs.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="topbar-shop-link"
        className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-[#007AFF] px-2.5 py-1 rounded-full border border-slate-200 hover:border-[#007AFF]/40 transition-colors"
        title="Buy hand-made balls at shop.tudorballs.com"
      >
        <ShoppingBag className="w-3.5 h-3.5" />
        Shop hand-made balls
      </a>

      <WorkspaceSwitcher current="studio" />

      <div className="ml-auto flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hidden sm:inline-flex"
          onClick={undo}
          disabled={historyLen === 0}
          data-testid="btn-undo"
          title="Undo (⌘Z)"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hidden sm:inline-flex"
          onClick={redo}
          disabled={futureLen === 0}
          data-testid="btn-redo"
          title="Redo"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full hidden sm:inline-flex"
          onClick={reset}
          data-testid="btn-reset"
          title="Reset design"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          className="rounded-full ml-1 h-9 px-2 sm:px-4"
          onClick={onOpenGallery}
          data-testid="btn-gallery"
        >
          <FolderOpen className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Gallery</span>
        </Button>
        <SendToFreehandButton className="ml-1" />
        <Button
          className={`rounded-full ml-1 h-9 text-white px-2 sm:px-4 ${isPro ? 'bg-[#007AFF] hover:bg-[#0062CC]' : 'bg-slate-900 hover:bg-slate-800'}`}
          onClick={onExport}
          data-testid="btn-export"
        >
          {isPro ? <Download className="w-4 h-4 sm:mr-2" /> : <Lock className="w-4 h-4 sm:mr-2" />}
          <span className="hidden sm:inline">Export</span>
        </Button>
        {!isPro && (
          <Button
            className="rounded-full ml-1 h-9 bg-[#007AFF] hover:bg-[#0062CC] text-white px-2 sm:px-4"
            onClick={onOpenUpgrade}
            data-testid="btn-upgrade"
          >
            <Sparkles className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Upgrade · $9</span>
            <span className="sm:hidden text-xs font-semibold">$9</span>
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full ml-1 h-9 w-9 p-0 border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => setAdminOpen(true)}
            title="Admin"
            data-testid="btn-admin"
          >
            <Shield className="w-4 h-4" />
          </Button>
        )}
        {user ? (
          <button
            onClick={logout}
            className="ml-1 sm:ml-2 flex items-center gap-2 pl-1 pr-1 sm:pr-2 h-9 rounded-full border border-slate-200 hover:bg-slate-50"
            data-testid="btn-logout"
            title={user.email}
          >
            {user.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-slate-300" />
            )}
            <LogOut className="w-3.5 h-3.5 text-slate-400 hidden sm:block" />
          </button>
        ) : (
          <Button
            variant="ghost"
            className="rounded-full ml-1 h-9 px-2 sm:px-4"
            onClick={login}
            data-testid="btn-login"
          >
            <LogIn className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Sign in</span>
          </Button>
        )}
      </div>
      {isAdmin && <AdminDrawer open={adminOpen} onOpenChange={setAdminOpen} />}
    </header>
  );
}
